import { ProductType } from "app/model/entities/entity-model";

export interface ImageMatchResult {
  file: File;
  matchedProductType: ProductType | null;
  matchedBy: 'exact-style' | 'style-prefix' | 'manual' | null;
  existingImageCount: number;
  _setAsPrimary?: boolean;
  _skip?: boolean;
  _processed?: boolean;
  _thumbnailUrl?: string;
}

export class ImageMatchFns {

  /**
   * Match files to ProductTypes by extracting the style (SKU) from the filename.
   *
   * When a specific manufacturer is selected, productTypes should be pre-filtered
   * so ambiguity is impossible. When "All Manufacturers" is selected, ambiguous
   * styles (same style on multiple manufacturers) return no match.
   *
   * Strategy (priority order):
   * 1. Exact style: filename stem equals style (case-insensitive)
   * 2. Style prefix: stem starts with style + separator (- or _)
   */
  static matchFiles(files: File[], productTypes: ProductType[]): ImageMatchResult[] {
    const byStyleLower = new Map<string, ProductType[]>();
    for (const pt of productTypes) {
      const key = pt.style?.toLowerCase().trim();
      if (!key) continue;
      const arr = byStyleLower.get(key) || [];
      arr.push(pt);
      byStyleLower.set(key, arr);
    }

    return files.map(file => {
      const stem = this.getFilenameStem(file.name).toLowerCase().trim();

      // Strategy 1: Exact match on style
      const exact = byStyleLower.get(stem);
      if (exact?.length === 1) {
        return this.createResult(file, exact[0], 'exact-style');
      }

      // Strategy 2: Style prefix — stem starts with style + separator
      // Find the longest matching style to avoid false positives
      let bestMatch: ProductType | null = null;
      let bestLength = 0;
      let ambiguous = false;

      for (const [style, pts] of byStyleLower) {
        if (style.length <= bestLength) continue;
        if (!stem.startsWith(style)) continue;
        if (stem.length === style.length) continue; // exact match handled above

        const nextChar = stem[style.length];
        if (nextChar !== '-' && nextChar !== '_') continue;

        if (pts.length === 1) {
          bestMatch = pts[0];
          bestLength = style.length;
          ambiguous = false;
        } else {
          // Multiple product types share this style — ambiguous
          if (style.length > bestLength) {
            ambiguous = true;
            bestLength = style.length;
            bestMatch = null;
          }
        }
      }

      if (bestMatch && !ambiguous) {
        return this.createResult(file, bestMatch, 'style-prefix');
      }

      return this.createResult(file, null, null);
    });
  }

  static getFilenameStem(filename: string): string {
    const dot = filename.lastIndexOf('.');
    return dot > 0 ? filename.substring(0, dot) : filename;
  }

  private static createResult(
    file: File,
    pt: ProductType | null,
    matchedBy: ImageMatchResult['matchedBy']
  ): ImageMatchResult {
    return {
      file,
      matchedProductType: pt,
      matchedBy,
      existingImageCount: pt?.productTypeImages?.filter(pti => !pti.isThumbnail).length ?? 0,
    };
  }
}
